'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { GraduationCap, HeartPulse, Leaf, ArrowRight, Clock } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface CauseConfig {
  id: string;
  icon: React.ElementType;
  title: string;
  tagline: string;
  description: string;
  urgency: string | null;
  color: string;
  bg: string;
  progressColor: string;
  tag: string;
  tagColor: string;
  highlights: string[];
  settingKey: string;
}

const causeConfigs: CauseConfig[] = [
  {
    id: 'cause-education',
    icon: GraduationCap,
    title: 'Educational Support',
    tagline: 'Empowering minds, shaping futures',
    description:
      'Fund scholarships, build classrooms, supply learning materials, and train teachers in underserved communities across three continents.',
    urgency: null,
    color: 'text-primary',
    bg: 'bg-primary/10',
    progressColor: 'bg-primary',
    tag: 'Education',
    tagColor: 'bg-primary/10 text-primary',
    highlights: ['2,400 Scholarships Funded', '68 Classrooms Built', '12 Countries Reached'],
    settingKey: 'cause_donation_education',
  },
  {
    id: 'cause-health',
    icon: HeartPulse,
    title: 'Health & Care Support',
    tagline: 'Healing communities, saving lives',
    description:
      'Provide medical equipment, fund mobile clinics, support maternal health programs, and deliver clean water access to rural populations.',
    urgency: null,
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    progressColor: 'bg-rose-500',
    tag: 'Health',
    tagColor: 'bg-rose-50 text-rose-600',
    highlights: ['18 Mobile Clinics Active', '94,000 Patients Treated', '6 Clean Water Systems'],
    settingKey: 'cause_donation_health',
  },
  {
    id: 'cause-environment',
    icon: Leaf,
    title: 'Environmental Support',
    tagline: 'Protecting the planet we share',
    description:
      'Plant trees, restore wetlands, fund renewable energy for villages, and run climate education programs for youth in high-risk regions.',
    urgency: null,
    color: 'text-accent',
    bg: 'bg-accent/10',
    progressColor: 'bg-accent',
    tag: 'Environment',
    tagColor: 'bg-accent/10 text-accent',
    highlights: ['320,000 Trees Planted', '14 Villages Solarized', '8 Wetlands Restored'],
    settingKey: 'cause_donation_environment',
  },
];

export default function CausesSection() {
  const [donationAmounts, setDonationAmounts] = useState<Record<string, string>>({
    cause_donation_education: '',
    cause_donation_health: '',
    cause_donation_environment: '',
  });

  useEffect(() => {
    const fetchAmounts = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('site_settings')
          .select('key, value')
          .in('key', ['cause_donation_education', 'cause_donation_health', 'cause_donation_environment']);

        if (data) {
          const map: Record<string, string> = {};
          data.forEach((row: { key: string; value: string }) => {
            map[row.key] = row.value;
          });
          setDonationAmounts(prev => ({ ...prev, ...map }));
        }
      } catch {
        // Silently fail — amounts just won't show
      }
    };
    fetchAmounts();
  }, []);

  return (
    <section className="py-20 lg:py-28 bg-secondary/40">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent bg-accent/10 px-4 py-1.5 rounded-full mb-4">
            Active Campaigns
          </span>
          <h2 className="section-title text-foreground mb-4">
            Choose Your Cause
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Three pillars of change. Every donation is allocated directly to your chosen cause with full transparency.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-3 gap-8">
          {causeConfigs.map((cause) => {
            const Icon = cause.icon;
            const donationAmount = donationAmounts[cause.settingKey];
            return (
              <div
                key={cause.id}
                className="bg-card rounded-2xl shadow-card border border-border overflow-hidden card-hover flex flex-col"
              >
                {/* Urgency Banner */}
                {cause.urgency && (
                  <div className="bg-rose-500 text-white text-xs font-semibold px-4 py-2 flex items-center gap-2">
                    <Clock size={13} />
                    {cause.urgency}
                  </div>
                )}

                <div className="p-7 flex flex-col flex-1">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-12 h-12 ${cause.bg} rounded-xl flex items-center justify-center`}>
                      <Icon size={22} className={cause.color} />
                    </div>
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${cause.tagColor}`}>
                      {cause.tag}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-foreground mb-1">{cause.title}</h3>
                  <p className="text-sm font-medium text-muted-foreground mb-3 italic">{cause.tagline}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">{cause.description}</p>

                  {/* Donation Amount (set by staff) */}
                  {donationAmount && (
                    <div className="mb-5 p-4 rounded-xl border" style={{ background: 'rgba(196,154,0,0.06)', borderColor: 'rgba(196,154,0,0.2)' }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#7a6030' }}>
                        Donation Required
                      </p>
                      <p className="text-2xl font-bold" style={{ color: '#C49A00' }}>
                        ${donationAmount}
                      </p>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="mt-auto">
                    <Link
                      href="/donation-screen"
                      className="flex items-center justify-center gap-2 w-full btn-primary font-semibold text-sm px-5 py-3 rounded-xl"
                    >
                      Support This Cause
                      <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}